import termsAndConditionsDocument from "../docs/TERMS-AND-CONDITIONS.md?raw";
import { translateUi } from "./i18n";
import { getLanguageMeta, readStoredLanguage } from "./i18n/languages";

export const TERMS_AND_CONDITIONS_FULL_TEXT = termsAndConditionsDocument;
/** Same placeholder string used in applySignaturePreview for empty name / job title. */
export const TERMS_SIGNATURE_VALUE_PLACEHOLDER = "______________________________";

export function formatTermsDocumentForDisplay(markdownText: string): string {
  const normalized = markdownText
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/^\s*-\s+/gm, "• ")
    .replace(/^\s*---\s*$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  const lines = normalized.split("\n");
  const merged: string[] = [];
  for (let i = 0; i < lines.length; i += 1) {
    const current = lines[i].trim();
    const next = lines[i + 1]?.trim() ?? "";
    if (/^\d+\.\d+$/.test(current) && next) {
      merged.push(`${current} ${next}`);
      i += 1;
      continue;
    }
    merged.push(lines[i]);
  }

  return merged.join("\n");
}

export function applySignaturePreview(
  formattedText: string,
  details: {
    fullName: string;
    jobTitle: string;
  }
): string {
  const fullName = details.fullName.trim() || TERMS_SIGNATURE_VALUE_PLACEHOLDER;
  const jobTitle = details.jobTitle.trim() || TERMS_SIGNATURE_VALUE_PLACEHOLDER;

  return formattedText
    .replace(/Signed electronically by \(Customer\):\s*.*/i, `Signed electronically by (Customer): ${fullName}`)
    .replace(/Job title:\s*.*/i, `Job title: ${jobTitle}`);
}

export function formatUkDateTime(value: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(value);
}

function escapeHtmlText(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export type TermsAgreementRecordAudit = {
  signerName: string;
  signerJobTitle: string;
  signerEmail: string;
  signatureDataUrl: string;
  companyName: string;
  companyCode: string;
  externalIp: string;
  termsAgreementStampedAt: string | null;
  termsAccepted: boolean;
  termsAcceptedAt: string | null;
  /** Historical or current terms markdown. Defaults to the bundled file. */
  markdown?: string;
  versionLabel?: string;
};

function termsRecordTryAuditLine(trimmed: string, audit: TermsAgreementRecordAudit): string | null {
  if (/^Email address:/i.test(trimmed)) {
    return `<p><strong>Email address:</strong> ${escapeHtmlText(audit.signerEmail)}</p>`;
  }
  if (/^Full name:/i.test(trimmed)) {
    return `<p><strong>Full name:</strong> ${escapeHtmlText(audit.signerName.trim())}</p>`;
  }
  if (/^Date \(UK time\):/i.test(trimmed)) {
    return `<p><strong>Date (UK time):</strong> ${escapeHtmlText(formatUkDateTime(new Date()))}</p>`;
  }
  if (/^Acceptance timestamp \(UK time\):/i.test(trimmed)) {
    const when =
      audit.termsAgreementStampedAt != null
        ? formatUkDateTime(new Date(audit.termsAgreementStampedAt))
        : audit.termsAccepted
          ? formatUkDateTime(new Date())
          : "—";
    return `<p><strong>Acceptance timestamp (UK time):</strong> ${escapeHtmlText(when)}</p>`;
  }
  if (/^External IP address:/i.test(trimmed)) {
    return `<p><strong>External IP address:</strong> ${escapeHtmlText(audit.externalIp)}</p>`;
  }
  if (/^IP address:/i.test(trimmed)) {
    return `<p><strong>IP address:</strong> ${escapeHtmlText(audit.externalIp)}</p>`;
  }
  return null;
}

/** Printable / downloadable HTML: full terms text, filled signature block, and audit lines. */
export function buildTermsAgreementRecordHtml(audit: TermsAgreementRecordAudit): string {
  const sourceMarkdown = (audit.markdown && audit.markdown.trim()) || TERMS_AND_CONDITIONS_FULL_TEXT;
  const merged = applySignaturePreview(formatTermsDocumentForDisplay(sourceMarkdown), {
    fullName: audit.signerName,
    jobTitle: audit.signerJobTitle,
  });
  const lines = merged.split("\n");
  const parts: string[] = [];
  const meta = getLanguageMeta(readStoredLanguage());
  parts.push(`<!DOCTYPE html><html lang="${meta.code}" dir="${meta.dir}"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${escapeHtmlText(translateUi("Rail Intel Terms — record copy"))}</title>
<style>
  body { font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; color: #0f172a; font-size: 11pt; line-height: 1.45; max-width: 42rem; margin: 0 auto; padding: 12mm; }
  .record-header { border-bottom: 1px solid #e2e8f0; padding-bottom: 12px; margin-bottom: 1rem; }
  .record-header h1 { font-size: 14pt; margin: 0 0 6px; font-weight: 700; }
  .record-meta { font-size: 9pt; color: #64748b; margin: 0; line-height: 1.4; }
  p { margin: 0 0 0.45em; }
  .sp { min-height: 10px; }
  hr { border: none; border-top: 1px solid #e2e8f0; margin: 14px 0; }
  .sig-wrap { margin: 12px 0 16px; }
  img.sig { max-height: 140px; border: 1px solid #cbd5e1; background: #fff; display: block; margin-top: 8px; }
  .audit-note { margin-top: 1.25rem; padding: 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 9pt; line-height: 1.45; }
  .no-sig { color: #64748b; font-style: italic; font-size: 10pt; }
  @media print { body { padding: 8mm; } }
</style></head><body>`);
  parts.push(`<div class="record-header"><h1>${escapeHtmlText(translateUi("Rail Intel Ltd — Terms & Conditions (record copy)"))}</h1>`);
  parts.push(
    `<p class="record-meta">${escapeHtmlText(translateUi("Company"))}: <strong>${escapeHtmlText(audit.companyName || audit.companyCode)}</strong> · ${escapeHtmlText(translateUi("Company code"))}: <strong>${escapeHtmlText(
      audit.companyCode
    )}</strong>${audit.versionLabel ? ` · ${escapeHtmlText(translateUi("Version"))}: <strong>${escapeHtmlText(audit.versionLabel)}</strong>` : ""}<br/>${escapeHtmlText(translateUi("Signer email"))}: ${escapeHtmlText(audit.signerEmail)} · ${escapeHtmlText(translateUi("Copy generated (UK time)"))}: ${escapeHtmlText(
      formatUkDateTime(new Date())
    )}</p></div>`
  );

  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const trimmed = line.trim();
    const auditHtml = termsRecordTryAuditLine(trimmed, audit);
    if (auditHtml) {
      parts.push(auditHtml);
      i += 1;
      continue;
    }
    if (!trimmed) {
      parts.push(`<div class="sp"></div>`);
      i += 1;
      continue;
    }
    if (/^---+$/m.test(trimmed)) {
      parts.push("<hr/>");
      i += 1;
      continue;
    }

    const sigStart = trimmed.match(/^(Signed electronically by \(Customer\)|Job title):\s*(.*)$/);
    if (sigStart) {
      const sigRows: { label: string; value: string }[] = [];
      let j = i;
      while (j < lines.length) {
        const Lt = (lines[j] ?? "").trim();
        if (!Lt) {
          j += 1;
          continue;
        }
        const m = Lt.match(/^(Signed electronically by \(Customer\)|Job title):\s*(.*)$/);
        if (!m) break;
        sigRows.push({ label: m[1]!, value: m[2] ?? "" });
        j += 1;
      }
      const customerRow = sigRows.find((r) => r.label === "Signed electronically by (Customer)");
      const jobRow = sigRows.find((r) => r.label === "Job title");
      parts.push(`<div class="sig-wrap">`);
      if (customerRow) {
        parts.push(
          `<p><strong>${escapeHtmlText(customerRow.label)}:</strong> ${escapeHtmlText(customerRow.value.trim())}</p>`
        );
        if (audit.signatureDataUrl.trim()) {
          parts.push(`<img class="sig" src="${audit.signatureDataUrl}" alt="Electronic signature"/>`);
        } else {
          parts.push(`<p class="no-sig">No signature image was stored for this agreement.</p>`);
        }
      }
      if (jobRow) {
        parts.push(`<p><strong>${escapeHtmlText(jobRow.label)}:</strong> ${escapeHtmlText(jobRow.value.trim())}</p>`);
      }
      parts.push(`</div>`);
      i = j;
      continue;
    }

    parts.push(`<p>${escapeHtmlText(line)}</p>`);
    i += 1;
  }

  parts.push(`<div class="audit-note"><strong>Acceptance status in this copy:</strong> `);
  if (audit.termsAccepted && audit.termsAgreementStampedAt) {
    parts.push(
      `Tick-box confirmed in the application at ${escapeHtmlText(
        formatUkDateTime(new Date(audit.termsAgreementStampedAt))
      )} (UK time).`
    );
  } else {
    parts.push("Tick-box acceptance was not yet confirmed in the application when this copy was generated.");
  }
  if (audit.termsAcceptedAt) {
    parts.push(
      ` Server-recorded acceptance: ${escapeHtmlText(new Date(audit.termsAcceptedAt).toLocaleString("en-GB"))}.`
    );
  }
  parts.push(`</div></body></html>`);
  return parts.join("");
}

/** Opens a print-ready copy; the browser print dialog can Save as PDF. */
export function openTermsAgreementPdfDownload(html: string, suggestedBasename?: string): void {
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (!w) {
    URL.revokeObjectURL(url);
    return;
  }
  const suggestedTitle = `${suggestedBasename || "terms-agreement"}.pdf`;
  const printDoc = () => {
    try {
      w.document.title = suggestedTitle;
      w.focus();
      w.print();
    } catch {
      /* ignore */
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
  };
  window.setTimeout(printDoc, 600);
}
